import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, BookOpen, Calendar, Clock, Eye } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { storiesApi } from "@/api/stories";
import { Story } from "@/types/database";

const Stories = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [stories, setStories] = useState<Story[]>([]);
  const [loadingStories, setLoadingStories] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (user) {
      loadStories();
    }
  }, [user]);

  const loadStories = async () => {
    try {
      setLoadingStories(true);
      const result = await storiesApi.getUserStories();
      
      if (result.success && result.data) {
        setStories(result.data);
      } else {
        setError(result.error || 'Failed to load stories');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoadingStories(false);
    }
  };

  const filteredStories = stories.filter(story => 
    story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (story.description && story.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
    story.genre.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getGenreEmoji = (genre: string) => {
    const emojis: { [key: string]: string } = {
      adventure: "🗺️",
      fantasy: "🐉",
      educational: "📚",
      bedtime: "🌙",
      friendship: "🤝",
      nature: "🌿"
    };
    return emojis[genre] || "📖";
  };

  const getGenreColor = (genre: string) => {
    const colors: { [key: string]: string } = {
      adventure: "bg-blue-100 text-blue-800",
      fantasy: "bg-purple-100 text-purple-800",
      educational: "bg-green-100 text-green-800",
      bedtime: "bg-indigo-100 text-indigo-800",
      friendship: "bg-pink-100 text-pink-800",
      nature: "bg-emerald-100 text-emerald-800"
    };
    return colors[genre] || "bg-gray-100 text-gray-800";
  };

  // If still loading auth, show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <BookOpen className="h-8 w-8 animate-pulse text-purple-500" />
          <span className="text-lg">Loading your stories...</span>
        </div>
      </div>
    );
  }

  // If not logged in, redirect
  if (!user) {
    navigate("/auth");
    return null;
  }

  return (
    <Layout>
      <div className="py-6 px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">My Stories</h1>
            <p className="text-muted-foreground">
              Your collection of personalized storybooks
            </p>
          </div>
          
          <div className="flex w-full md:w-auto gap-2">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search stories..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button onClick={() => navigate("/")}>
              <Plus className="h-4 w-4 mr-1" />
              Create New
            </Button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loadingStories ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <BookOpen className="h-8 w-8 animate-pulse text-purple-500" />
              <span className="text-lg">Loading your stories...</span>
            </div>
          </div>
        ) : filteredStories.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? "No stories found" : "No stories yet"}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery 
                ? "Try adjusting your search terms" 
                : "Create your first personalized storybook"
              }
            </p>
            {!searchQuery && (
              <Button onClick={() => navigate("/")}>
                <Plus className="h-4 w-4 mr-1" />
                Create Your First Story
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStories.map((story) => (
              <Card key={story.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getGenreEmoji(story.genre)}</span>
                      <Badge variant="secondary" className={getGenreColor(story.genre)}>
                        {story.genre}
                      </Badge>
                    </div>
                    {!story.is_published && (
                      <Badge variant="outline" className="text-orange-600 border-orange-200">
                        Draft
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg">{story.title}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {story.description || "A magical story for children"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(story.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{story.read_time_minutes} min read</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Eye className="h-4 w-4" />
                      <span>{story.age_group} years</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => navigate(`/stories/${story.id}`)}
                    >
                      <BookOpen className="h-4 w-4 mr-1" />
                      Read
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => navigate(`/stories/${story.id}/edit`)}
                    >
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Stories; 